import { useState, useEffect, useCallback } from 'react';
import { useCustomer } from '../contexts/CustomerContext';
import { useKeycloak } from '../contexts/KeycloakContext';
import { isAdmin } from '../config/roles';
import { api, condoApi, fetchWithAuth } from '../services/api';

export interface SystemStatusData {
  isGeneralMode: boolean;
  contextName: string;
  contextType?: string;
  stats: {
    totalCustomers?: number;
    totalPeople: number;
    totalZones: number;
    totalDevices: number;
    todayEvents: number;
  };
  devices: {
    online: number;
    offline: number;
    total: number;
    percentage: number;
  };
  zones?: {
    active: number;
    inactive: number;
    total: number;
  };
  customers?: {
    active: number;
    inactive: number;
    total: number;
  };
}

interface FetchSystemStatusOptions {
  isGeneralMode: boolean;
  customerId?: string;
  customerName?: string;
  customerType?: string;
}

const fetchSystemStatus = async (options: FetchSystemStatusOptions): Promise<SystemStatusData> => {
  const { isGeneralMode, customerId, customerName, customerType } = options;

  if (isGeneralMode) {
    // General mode: fetch system-wide data
    // _silentOnError: true prevents the axios interceptor from showing an error toast
    // for background status fetches — the user shouldn't see a toast for these.
    const [statsResponse, customersResponse] = await Promise.all([
      api.get('/dashboard/global-summary', { _silentOnError: true } as any),
      condoApi.getCustomers(),
    ]);

    const customers = Array.isArray(customersResponse?.data?.data)
      ? customersResponse.data.data
      : Array.isArray(customersResponse?.data)
        ? customersResponse.data
        : [];
    const stats = statsResponse.data || {};

    // Aggregate device data across all customers
    let totalDevices = 0;
    let onlineDevices = 0;

    // Fetch devices for each customer (limit to avoid overwhelming the system)
    const devicePromises = (customers.length > 0 ? customers.slice(0, 10) : []).map(async (customer: any) => {
      try {
        const response = await fetchWithAuth(`/api/v1/iot-devices/customer/${customer.id}`);
        const data = await response.json();
        const devices = data.devices || [];
        totalDevices += devices.length;
        // Device status is a string: 'online', 'offline', 'error', 'maintenance'
        onlineDevices += devices.filter((d: any) => d.status === 'online').length;
      } catch (error) {
        console.error(`Failed to fetch devices for customer ${customer.id}`, error);
      }
    });

    await Promise.allSettled(devicePromises);

    return {
      isGeneralMode: true,
      contextName: 'Sistema - Visualização Geral',
      stats: {
        totalCustomers: customers.length,
        totalPeople: stats.total_people || 0,
        totalZones: stats.total_zones || 0,
        totalDevices: totalDevices,
        todayEvents: stats.today_events || 0,
      },
      devices: {
        online: onlineDevices,
        offline: totalDevices - onlineDevices,
        total: totalDevices,
        percentage: totalDevices > 0 ? Math.round((onlineDevices / totalDevices) * 100) : 0,
      },
      customers: {
        active: customers.filter((c: any) => c.status === 'active').length,
        inactive: customers.filter((c: any) => c.status !== 'active').length,
        total: customers.length,
      },
    };
  } else {
    // Customer mode: fetch customer-specific data
    if (!customerId) {
      throw new Error('Customer ID is required for customer mode');
    }

    const [statsResponse, zonesResponse, devicesResponse] = await Promise.all([
      api.get('/dashboard/stats', { params: { customer_id: customerId }, _silentOnError: true } as any),
      fetchWithAuth(`/api/v1/zones/customer/${customerId}`),
      fetchWithAuth(`/api/v1/iot-devices/customer/${customerId}`),
    ]);

    const stats = statsResponse.data || {};
    const zonesData = await zonesResponse.json();
    const devicesData = await devicesResponse.json();
    const zones = zonesData.zones || [];
    const devices = devicesData.devices || [];

    // Device status is a string: 'online', 'offline', 'error', 'maintenance'
    const onlineDevices = devices.filter((d: any) => d.status === 'online').length;
    const activeZones = zones.filter((z: any) => z.status === 'active').length;

    return {
      isGeneralMode: false,
      contextName: customerName || 'Cliente',
      contextType: customerType,
      stats: {
        totalPeople: stats.total_people || 0,
        totalZones: zones.length,
        totalDevices: devices.length,
        todayEvents: stats.today_events || 0,
      },
      devices: {
        online: onlineDevices,
        offline: devices.length - onlineDevices,
        total: devices.length,
        percentage: devices.length > 0 ? Math.round((onlineDevices / devices.length) * 100) : 0,
      },
      zones: {
        active: activeZones,
        inactive: zones.length - activeZones,
        total: zones.length,
      },
    };
  }
};

// Stale threshold: if data is older than this, fetch fresh data on hover
const STALE_THRESHOLD_MS = 10000; // 10 seconds

export const useSystemStatus = (autoRefreshInterval: number = 30000) => {
  const { selectedCustomer } = useCustomer();
  const { roles } = useKeycloak();
  const userIsAdmin = isAdmin(roles);
  const isGeneralMode = userIsAdmin && selectedCustomer === null;

  const [data, setData] = useState<SystemStatusData | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [lastFetchTime, setLastFetchTime] = useState<number>(0);

  const fetchData = useCallback(async () => {
    // Customer context hasn't loaded yet — skip silently
    if (!isGeneralMode && !selectedCustomer?.id) return;
    try {
      setError(null);
      const statusData = await fetchSystemStatus({
        isGeneralMode,
        customerId: selectedCustomer?.id,
        customerName: selectedCustomer?.name,
        customerType: selectedCustomer?.type,
      });
      setData(statusData);
      setLastFetchTime(Date.now());
    } catch (err) {
      console.error('Failed to fetch system status:', err);
      setError(err instanceof Error ? err : new Error('Unknown error'));
    } finally {
      setIsLoading(false);
    }
  }, [isGeneralMode, selectedCustomer]);

  // Refresh if data is stale (older than STALE_THRESHOLD_MS)
  // Call this when user hovers over the status indicator
  const refreshIfStale = useCallback(() => {
    const now = Date.now();
    if (now - lastFetchTime > STALE_THRESHOLD_MS) {
      fetchData();
    }
  }, [lastFetchTime, fetchData]);

  // Initial fetch
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-refresh
  useEffect(() => {
    if (autoRefreshInterval > 0) {
      const interval = setInterval(fetchData, autoRefreshInterval);
      return () => clearInterval(interval);
    }
  }, [fetchData, autoRefreshInterval]);

  return {
    data,
    isLoading,
    error,
    isGeneralMode,
    refresh: fetchData,
    refreshIfStale,
    lastFetchTime,
  };
};
